import { useEffect, useState } from 'react';
const DIALOG_SELECTOR = '[role="dialog"], [role="alertdialog"]';
const isHiddenFromAssistiveTechnology = (element) => element.getAttribute('aria-hidden') === 'true' || element.hasAttribute('inert');
const isDrawn = (element) => {
    if (getComputedStyle(element).visibility === 'hidden') {
        return false;
    }
    for (let node = element; node && node !== document.body; node = node.parentElement) {
        if (getComputedStyle(node).display === 'none') {
            return false;
        }
    }
    return true;
};
// Third-party frames and closed drawers carry aria-hidden while no modal is open
const isHiddenByModal = (layer) => isHiddenFromAssistiveTechnology(layer) && !(layer instanceof HTMLIFrameElement) && isDrawn(layer);
/**
 * The dialog of the modal on top, or nothing when no modal is open.
 *
 * A modal hides every other top-level element from assistive technology, so the first top-level
 * element still exposed that holds a dialog is the modal on top. A popup with a dialog role that
 * hides nothing, such as a date picker or a coach mark, is not one. Moving the toasts into it would
 * place them relative to its transformed box instead of the viewport, and gain nothing, because its
 * background is still exposed.
 */
const findModalDialog = (host) => {
    const layers = Array.from(document.body.children).filter((child) => child instanceof HTMLElement && child !== host);
    if (!layers.some(isHiddenByModal)) {
        return undefined;
    }
    for (const layer of layers) {
        if (isHiddenFromAssistiveTechnology(layer)) {
            continue;
        }
        // A photo viewer, for one, keeps an undrawn dialog in the page at all times
        const dialogs = layer.matches(DIALOG_SELECTOR)
            ? [layer]
            : Array.from(layer.querySelectorAll(DIALOG_SELECTOR));
        const dialog = dialogs.find(isDrawn);
        if (dialog) {
            return dialog;
        }
    }
    return undefined;
};
/**
 * Toasts remain available inside the active modal, whose background is hidden from assistive technology.
 * @returns A stable portal host that follows the active modal without remounting its toasts.
 */
export const useToastPortal = () => {
    const [container] = useState(() => {
        const host = document.createElement('div');
        // The top layer keeps the toasts above an overlay that a modal opens with a higher z-index,
        // such as a legacy modal, while the host stays inside the modal for assistive technology
        host.setAttribute('popover', 'manual');
        host.style.cssText =
            'position: absolute; inset: auto; width: 0; height: 0; margin: 0; padding: 0; border: 0; overflow: visible; background: transparent;';
        return host;
    });
    useEffect(() => {
        const updateContainer = () => {
            var _a;
            const parent = (_a = findModalDialog(container)) !== null && _a !== void 0 ? _a : document.body;
            if (container.parentElement !== parent) {
                parent.appendChild(container);
                // Leaving the document takes a popover out of the top layer
                if (typeof container.showPopover === 'function') {
                    container.showPopover();
                }
            }
            // A modal hides its body-level siblings, the host included, when it opens
            if (container.hasAttribute('aria-hidden')) {
                container.removeAttribute('aria-hidden');
            }
        };
        updateContainer();
        // Modals mount and unmount as body children, so the body's own child list is the signal.
        // Observing the whole subtree would run this on every DOM change in the application.
        const layerObserver = new MutationObserver(updateContainer);
        layerObserver.observe(document.body, { childList: true });
        const visibilityObserver = new MutationObserver(updateContainer);
        visibilityObserver.observe(document.body, {
            attributes: true,
            subtree: true,
            attributeFilter: ['aria-hidden', 'inert'],
        });
        return () => {
            layerObserver.disconnect();
            visibilityObserver.disconnect();
            container.remove();
        };
    }, [container]);
    return container;
};
